import { useState, useCallback } from "react";
import {
  ComplianceTypeEntity,
  EnabledComplianceTypeEntity,
  GroupedCalendarEntries,
  SyncStatusEntity,
} from "../../../compliance/domain/entities";
import { TenantComplianceRepositoryImpl } from "../../../compliance/data/repositories";
import {
  GetMasterTypesUseCase,
  EnableTypeUseCase,
  DisableTypeUseCase,
  GetCalendarGroupedUseCase,
  GetSyncStatusUseCase,
  ManualSyncUseCase,
} from "../../domain/use-cases/compliance";

// Create repository instance (singleton)
const complianceRepository = new TenantComplianceRepositoryImpl();

// Create use cases
const getMasterTypesUseCase = new GetMasterTypesUseCase(complianceRepository);
const enableTypeUseCase = new EnableTypeUseCase(complianceRepository);
const disableTypeUseCase = new DisableTypeUseCase(complianceRepository);
const getCalendarGroupedUseCase = new GetCalendarGroupedUseCase(
  complianceRepository
);
const getSyncStatusUseCase = new GetSyncStatusUseCase(complianceRepository);
const manualSyncUseCase = new ManualSyncUseCase(complianceRepository);

export interface UseTenantComplianceReturn {
  // Master Types (includes enabled status)
  masterTypes: ComplianceTypeEntity[];
  loadingMasterTypes: boolean;
  errorMasterTypes: Error | null;
  fetchMasterTypes: () => Promise<void>;

  // Enable/Disable Actions
  enableComplianceType: (type: ComplianceTypeEntity) => Promise<EnabledComplianceTypeEntity>;
  disableComplianceType: (id: number) => Promise<void>;

  // Calendar
  groupedEntries: GroupedCalendarEntries;
  loadingCalendar: boolean;
  errorCalendar: Error | null;
  fetchCalendarGrouped: () => Promise<void>;

  // Sync
  syncStatus: SyncStatusEntity | null;
  loadingSyncStatus: boolean;
  errorSyncStatus: Error | null;
  syncing: boolean;
  fetchSyncStatus: () => Promise<void>;
  triggerManualSync: () => Promise<{
    success: boolean;
    message: string;
    data: any;
  }>;
}

export const useTenantCompliance = (): UseTenantComplianceReturn => {
  // Master Types State (includes enabled status)
  const [masterTypes, setMasterTypes] = useState<ComplianceTypeEntity[]>([]);
  const [loadingMasterTypes, setLoadingMasterTypes] = useState<boolean>(false);
  const [errorMasterTypes, setErrorMasterTypes] = useState<Error | null>(null);

  // Calendar State
  const [groupedEntries, setGroupedEntries] = useState<GroupedCalendarEntries>(
    {}
  );
  const [loadingCalendar, setLoadingCalendar] = useState<boolean>(false);
  const [errorCalendar, setErrorCalendar] = useState<Error | null>(null);

  // Sync State
  const [syncStatus, setSyncStatus] = useState<SyncStatusEntity | null>(null);
  const [loadingSyncStatus, setLoadingSyncStatus] = useState<boolean>(false);
  const [errorSyncStatus, setErrorSyncStatus] = useState<Error | null>(null);
  const [syncing, setSyncing] = useState<boolean>(false);

  // Master Types
  const fetchMasterTypes = useCallback(async () => {
    try {
      setLoadingMasterTypes(true);
      setErrorMasterTypes(null);
      const result = await getMasterTypesUseCase.execute();
      setMasterTypes(result);
    } catch (err) {
      setErrorMasterTypes(
        err instanceof Error
          ? err
          : new Error("Failed to fetch master compliance types")
      );
      throw err;
    } finally {
      setLoadingMasterTypes(false);
    }
  }, []);


  const enableComplianceType = useCallback(
    async (type: ComplianceTypeEntity) => {
      try {
        const result = await enableTypeUseCase.execute(type);
        
        // Update local state optimistically (no API refresh needed)
        setMasterTypes((prev) =>
          prev.map((t) =>
            t.id === type.id
              ? {
                  ...t,
                  is_enabled: true,
                  enabled_id: result.id, // tenant_compliances_enabled.id
                }
              : t
          )
        );
        
        return result;
      } catch (err) {
        // Error will be handled by the component
        throw err;
      }
    },
    []
  );

  const disableComplianceType = useCallback(
    async (id: number) => {
      try {
        await disableTypeUseCase.execute(id);
        
        // Update local state optimistically (no API refresh needed)
        setMasterTypes((prev) =>
          prev.map((t) =>
            t.enabled_id === id
              ? {
                  ...t,
                  is_enabled: false,
                  enabled_id: undefined,
                }
              : t
          )
        );
      } catch (err) {
        // Error will be handled by the component
        throw err;
      }
    },
    []
  );

  // Calendar
  const fetchCalendarGrouped = useCallback(async () => {
    try {
      setLoadingCalendar(true);
      setErrorCalendar(null);
      const result = await getCalendarGroupedUseCase.execute();
      setGroupedEntries(result);
    } catch (err) {
      setErrorCalendar(
        err instanceof Error
          ? err
          : new Error("Failed to fetch grouped calendar entries")
      );
      throw err;
    } finally {
      setLoadingCalendar(false);
    }
  }, []);

  // Sync
  const fetchSyncStatus = useCallback(async () => {
    try {
      setLoadingSyncStatus(true);
      setErrorSyncStatus(null);
      const result = await getSyncStatusUseCase.execute();
      setSyncStatus(result);
    } catch (err) {
      setErrorSyncStatus(
        err instanceof Error ? err : new Error("Failed to fetch sync status")
      );
      throw err;
    } finally {
      setLoadingSyncStatus(false);
    }
  }, []);

  const triggerManualSync = useCallback(async () => {
    try {
      setSyncing(true);
      setErrorSyncStatus(null);
      const result = await manualSyncUseCase.execute();
      // Refresh sync status and calendar after sync
      await Promise.all([fetchSyncStatus(), fetchCalendarGrouped()]);
      return result;
    } catch (err) {
      setErrorSyncStatus(
        err instanceof Error
          ? err
          : new Error("Failed to sync compliance calendar")
      );
      throw err;
    } finally {
      setSyncing(false);
    }
  }, [fetchSyncStatus, fetchCalendarGrouped]);

  return {
    // Master Types (includes enabled status)
    masterTypes,
    loadingMasterTypes,
    errorMasterTypes,
    fetchMasterTypes,

    // Enable/Disable Actions
    enableComplianceType,
    disableComplianceType,

    // Calendar
    groupedEntries,
    loadingCalendar,
    errorCalendar,
    fetchCalendarGrouped,

    // Sync
    syncStatus,
    loadingSyncStatus,
    errorSyncStatus,
    syncing,
    fetchSyncStatus,
    triggerManualSync,
  };
};
